import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CircleCheck as CheckCircle2, Copy, Check, ShoppingCart, CircleAlert as AlertCircle } from 'lucide-react'

interface SaveToCartDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  configId: string | null
  label: string | null
  price: number | null
  error: string | null
}

export function SaveToCartDialog({
  open,
  onOpenChange,
  configId,
  label,
  price,
  error,
}: SaveToCartDialogProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!configId) return
    await navigator.clipboard.writeText(configId)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#212b4f]">
            <ShoppingCart className="h-5 w-5" />
            {error ? 'Save Failed' : 'Configuration Saved'}
          </DialogTitle>
        </DialogHeader>

        {error ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 p-4">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">Could not save configuration</p>
                <p className="text-xs text-red-600 mt-1">{error}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-2xl border-[#212b4f] text-[#212b4f]"
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Configuration saved successfully
                </p>
                {label && (
                  <p className="text-xs text-green-700 mt-0.5">{label}</p>
                )}
                {price !== null && (
                  <p className="text-xs text-green-700 mt-0.5 font-semibold">
                    €{Math.round(price)}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Configuration ID
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded-lg bg-gray-100 border border-gray-200 px-3 py-2 text-sm font-mono text-gray-800 select-all break-all">
                  {configId}
                </code>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleCopy}
                  className="shrink-0 rounded-xl border-[#212b4f] text-[#212b4f] hover:bg-[#212b4f] hover:text-white h-9 w-9"
                  title="Copy config ID"
                >
                  {copied ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Use this ID in WooCommerce to retrieve the configuration and price via the API.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => onOpenChange(false)}
                className="rounded-2xl bg-[#212b4f] text-white hover:bg-[#1a2240]"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
